/**
 * `/family` — the parent / guardian view (P-SCH-K).
 *
 * A linked guardian sees the students attached to their account and, for the
 * selected child, that student's attendance log and recent quiz results — all
 * read-only. The data comes from three procs under
 * `learning.learner.guardian`:
 *   - `children()`               → the students linked to this guardian.
 *   - `childAttendance({ … })`   → date · status · course · note rows.
 *   - `childResults({ … })`      → quiz · course · score% · submitted-at rows.
 *
 * When no students are linked yet we render an `<EmptyState>` (a coaching
 * center links a student to the guardian account server-side; there's no
 * self-link here). With one or more children, a pill selector picks the active
 * child (default = the first), and the two read-only sections re-query for the
 * chosen `studentUserId`.
 *
 * Status / result vocabulary is inlined (string checks + a label/variant map) —
 * this SPA must never import `@mangrove/core`.
 */
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import {
  CalendarCheck,
  CheckCircle2,
  ClipboardList,
  Clock,
  GraduationCap,
  TrendingUp,
  Users,
  XCircle,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  KpiCard,
  Skeleton,
  cn,
} from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { RequireAuth } from '../lib/auth.js';
import { LearnShell } from '../components/shell.js';

export const Route = createFileRoute('/family/')({
  component: FamilyPage,
});

type Child =
  inferRouterOutputs<AppRouter>['learning']['learner']['guardian']['children']['children'][number];
type AttendanceRow =
  inferRouterOutputs<AppRouter>['learning']['learner']['guardian']['childAttendance']['rows'][number];
type ResultRow =
  inferRouterOutputs<AppRouter>['learning']['learner']['guardian']['childResults']['rows'][number];

function FamilyPage(): React.JSX.Element {
  return (
    <RequireAuth>
      <LearnShell title="Family" wide>
        <FamilyContent />
      </LearnShell>
    </RequireAuth>
  );
}

function FamilyContent(): React.JSX.Element {
  const childrenQ = trpc.learning.learner.guardian.children.useQuery();
  const children = childrenQ.data?.children ?? [];

  // Default to the first child; keep the selection valid if the list changes
  // (a linked student removed server-side shouldn't strand a dead selection).
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const selected = React.useMemo(() => {
    if (children.length === 0) return null;
    const found = children.find((c) => c.studentUserId === selectedId);
    return found ?? children[0]!;
  }, [children, selectedId]);

  return (
    <div className="space-y-6">
      <div className="animate-fade-in space-y-1.5 rounded-xl border border-l-2 border-l-primary/40 bg-gradient-to-br from-grad-green to-grad-blue px-5 py-4 dark:from-grad-green/15 dark:to-grad-blue/15">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">Parent portal</p>
        <h2 className="text-xl font-semibold tracking-tight">Family overview</h2>
        <p className="max-w-prose text-sm text-muted-foreground">
          A clear snapshot of how your student is progressing — attendance and recent results.
        </p>
      </div>

      {childrenQ.isPending ? (
        <FamilySkeleton />
      ) : childrenQ.isError ? (
        <ErrorCard
          message="We couldn’t load your linked students. Please check your connection and try again."
          onRetry={() => void childrenQ.refetch()}
        />
      ) : children.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No linked students"
          description="When a coaching center links a student to your account, they’ll appear here."
        />
      ) : (
        <div className="space-y-6">
          {selected ? (
            <Card className="card-soft animate-fade-in-up overflow-hidden border-l-2 border-l-primary/40 bg-gradient-to-b from-primary/5 to-card">
              <CardContent className="flex flex-wrap items-center gap-4 p-5">
                <span
                  aria-hidden="true"
                  className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-primary text-xl font-bold tracking-tight text-primary-foreground shadow-sm"
                >
                  {childInitials(selected)}
                </span>
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="truncate text-lg font-semibold tracking-tight">
                    {childLabel(selected)}
                  </p>
                  <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <GraduationCap className="size-4" aria-hidden="true" />
                    {relationshipLabel(selected.relationship)}
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {children.length > 1 ? (
            <div
              role="tablist"
              aria-label="Select a student"
              className="ls-stagger flex flex-wrap gap-2"
            >
              {children.map((child) => (
                <ChildPill
                  key={child.studentUserId}
                  child={child}
                  active={selected?.studentUserId === child.studentUserId}
                  onSelect={() => setSelectedId(child.studentUserId)}
                />
              ))}
            </div>
          ) : null}

          {selected ? <ChildPanels child={selected} /> : null}
        </div>
      )}
    </div>
  );
}

/** A child name → friendly display, falling back to the relationship. */
function childLabel(child: Child): string {
  return child.name ?? `Student (${child.relationship})`;
}

/** Up to two uppercase initials from the child's display label. */
function childInitials(child: Child): string {
  const label = childLabel(child);
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '–';
  const first = parts[0]![0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '';
  return (first + last).toUpperCase() || '–';
}

/** "parent" → "Parent" — a friendly, capitalized relationship label. */
function relationshipLabel(relationship: string): string {
  if (!relationship) return 'Guardian';
  return relationship.charAt(0).toUpperCase() + relationship.slice(1);
}

function ChildPill({
  child,
  active,
  onSelect,
}: {
  child: Child;
  active: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onSelect}
      data-testid="family-child-pill"
      className={cn(
        'press min-h-touch rounded-full border px-4 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-primary bg-primary/10 font-semibold text-primary'
          : 'border-input bg-background text-muted-foreground hover:bg-muted/50',
      )}
    >
      {childLabel(child)}
    </button>
  );
}

/**
 * The KPI strip + two read-only sections for one selected child. Re-mounts on
 * `studentUserId` change (the `key` on the wrapper), so the inner queries
 * cleanly re-fire for the new student and loading states show per-child. Both
 * queries are lifted here so the KPI strip can summarize the real rows.
 */
function ChildPanels({ child }: { child: Child }): React.JSX.Element {
  const studentUserId = child.studentUserId;
  const attendanceQ = trpc.learning.learner.guardian.childAttendance.useQuery({ studentUserId });
  const resultsQ = trpc.learning.learner.guardian.childResults.useQuery({ studentUserId });

  const attendanceRows = attendanceQ.data?.rows ?? [];
  const resultRows = resultsQ.data?.rows ?? [];

  const stats = React.useMemo(
    () => computeFamilyStats(attendanceRows, resultRows),
    [attendanceRows, resultRows],
  );

  return (
    <div key={studentUserId} className="space-y-6">
      <div className="ls-stagger grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KpiCard
          accent="success"
          icon={CheckCircle2}
          label="Attendance"
          value={stats.attendanceTotal > 0 ? `${stats.attendanceRate}%` : '—'}
          hint={
            stats.attendanceTotal > 0
              ? `${stats.presentCount + stats.lateCount} of ${stats.attendanceTotal} sessions`
              : 'No sessions yet'
          }
          isLoading={attendanceQ.isPending}
        />
        <KpiCard
          accent="primary"
          icon={CalendarCheck}
          label="Present"
          value={String(stats.presentCount)}
          isLoading={attendanceQ.isPending}
        />
        <KpiCard
          accent={stats.lateCount > 0 ? 'warning' : 'primary'}
          icon={Clock}
          label="Late"
          value={String(stats.lateCount)}
          isLoading={attendanceQ.isPending}
        />
        <KpiCard
          accent={stats.gradedCount > 0 ? scoreTone(stats.averageScore) : 'primary'}
          icon={TrendingUp}
          label="Average score"
          value={stats.gradedCount > 0 ? `${stats.averageScore}%` : '—'}
          hint={stats.gradedCount > 0 ? `${stats.gradedCount} graded` : 'No graded results'}
          isLoading={resultsQ.isPending}
        />
      </div>

      <div className="ls-stagger grid grid-cols-1 gap-4 sm:grid-cols-2">
        <AttendanceCard
          rows={attendanceRows}
          isPending={attendanceQ.isPending}
          isError={attendanceQ.isError}
          onRetry={() => void attendanceQ.refetch()}
        />
        <ResultsCard
          rows={resultRows}
          isPending={resultsQ.isPending}
          isError={resultsQ.isError}
          onRetry={() => void resultsQ.refetch()}
        />
      </div>
    </div>
  );
}

/** Roll up the real attendance + result rows into the KPI-strip numbers. */
function computeFamilyStats(
  attendance: AttendanceRow[],
  results: ResultRow[],
): {
  attendanceRate: number;
  attendanceTotal: number;
  presentCount: number;
  lateCount: number;
  absentCount: number;
  averageScore: number;
  gradedCount: number;
} {
  const presentCount = attendance.filter((r) => r.status === 'present').length;
  const lateCount = attendance.filter((r) => r.status === 'late').length;
  const absentCount = attendance.filter((r) => r.status === 'absent').length;
  const attendanceTotal = attendance.length;
  // Present + late both count as "showed up" toward the attendance rate.
  const attendanceRate =
    attendanceTotal > 0 ? Math.round(((presentCount + lateCount) / attendanceTotal) * 100) : 0;

  const graded = results.filter((r) => r.status === 'graded' && r.scorePercent != null);
  const gradedCount = graded.length;
  const averageScore =
    gradedCount > 0
      ? Math.round(graded.reduce((sum, r) => sum + (r.scorePercent ?? 0), 0) / gradedCount)
      : 0;

  return {
    attendanceRate,
    attendanceTotal,
    presentCount,
    lateCount,
    absentCount,
    averageScore,
    gradedCount,
  };
}

/** SCORE TONE convention: >=80 success, 50-79 warning, <50 destructive. */
function scoreTone(score: number): 'success' | 'warning' | 'destructive' {
  if (score >= 80) return 'success';
  if (score >= 50) return 'warning';
  return 'destructive';
}

function AttendanceCard({
  rows,
  isPending,
  isError,
  onRetry,
}: {
  rows: AttendanceRow[];
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <Card className="card-soft flex flex-col border-l-2 border-l-primary/40">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CalendarCheck className="size-4" aria-hidden="true" />
          </span>
          Attendance
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1">
        {isPending ? (
          <RowSkeleton />
        ) : isError ? (
          <InlineError onRetry={onRetry} />
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No attendance recorded yet.
          </p>
        ) : (
          <ul className="divide-y">
            {rows.map((row, i) => (
              <AttendanceItem key={`${row.date}-${i}`} row={row} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AttendanceItem({ row }: { row: AttendanceRow }): React.JSX.Element {
  return (
    <li className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="font-medium text-foreground">{formatDate(row.date)}</p>
        {row.courseTitle ? (
          <p className="truncate text-xs text-muted-foreground">{row.courseTitle}</p>
        ) : null}
        {row.note ? (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{row.note}</p>
        ) : null}
      </div>
      <AttendanceBadge status={row.status} />
    </li>
  );
}

/** present | absent | late | excused (inlined vocabulary). */
function AttendanceBadge({ status }: { status: string }): React.JSX.Element {
  const map: Record<
    string,
    { label: string; variant: 'success' | 'secondary' | 'warning' | 'outline' | 'destructive' }
  > = {
    present: { label: 'Present', variant: 'success' },
    late: { label: 'Late', variant: 'warning' },
    excused: { label: 'Excused', variant: 'secondary' },
    absent: { label: 'Absent', variant: 'destructive' },
  };
  const entry = map[status] ?? { label: status, variant: 'outline' as const };
  return (
    <Badge variant={entry.variant} className="mt-0.5 shrink-0">
      {entry.label}
    </Badge>
  );
}

function ResultsCard({
  rows,
  isPending,
  isError,
  onRetry,
}: {
  rows: ResultRow[];
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <Card className="card-soft flex flex-col border-l-2 border-l-primary/40">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ClipboardList className="size-4" aria-hidden="true" />
          </span>
          Recent results
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1">
        {isPending ? (
          <RowSkeleton />
        ) : isError ? (
          <InlineError onRetry={onRetry} />
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No results yet.</p>
        ) : (
          <ul className="divide-y">
            {rows.map((row, i) => (
              <ResultItem key={`${row.quizTitle}-${i}`} row={row} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ResultItem({ row }: { row: ResultRow }): React.JSX.Element {
  const graded = row.status === 'graded' && row.scorePercent != null;
  return (
    <li className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="truncate font-medium text-foreground">{row.quizTitle}</p>
        <p className="truncate text-xs text-muted-foreground">{row.courseTitle}</p>
        {row.submittedAt ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{formatDate(row.submittedAt)}</p>
        ) : null}
      </div>
      {graded ? (
        <span className="animate-count-up mt-0.5 shrink-0 tabular-nums text-sm font-semibold text-foreground">
          {formatScorePercent(row.scorePercent)}
        </span>
      ) : (
        <Badge variant="warning" className="mt-0.5 shrink-0">
          Pending
        </Badge>
      )}
    </li>
  );
}

/** "82.5%" → at most one decimal, no trailing ".0"; null → "—". */
function formatScorePercent(p: number | null): string {
  if (p == null) return '—';
  return `${Math.round(p * 10) / 10}%`;
}

/** Locale date from an ISO string (date-only 'YYYY-MM-DD' or full ISO). A
 *  date-only string is parsed in LOCAL time (constructed from parts) — `new
 *  Date('2026-06-14')` would parse as UTC midnight and render the previous day
 *  in any timezone behind UTC. Full ISO timestamps keep their instant. */
function formatDate(iso: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function RowSkeleton(): React.JSX.Element {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-6 w-16 shrink-0 rounded-full" />
        </div>
      ))}
    </div>
  );
}

function FamilySkeleton(): React.JSX.Element {
  return (
    <div className="space-y-6">
      <Card className="card-soft">
        <CardContent className="flex items-center gap-4 p-5">
          <Skeleton className="size-14 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-24" />
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Card key={i} className="card-soft">
            <CardContent className="space-y-2 p-5">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {Array.from({ length: 2 }, (_, i) => (
          <Card key={i} className="card-soft">
            <CardHeader className="pb-3">
              <Skeleton className="h-5 w-40" />
            </CardHeader>
            <CardContent>
              <RowSkeleton />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function InlineError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 py-6 text-center">
      <XCircle className="size-6 text-rose-500" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">Couldn’t load this. Please try again.</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function ErrorCard({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 px-6 py-8 text-center">
        <XCircle className="size-6 text-rose-500" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </CardContent>
    </Card>
  );
}
